<script setup>
/**
 * 通知項目元件 (NotificationItem.vue)
 *
 * `Notification` 的子元件，顯示單一通知的頭像、內容（HTML）與相對時間，
 * 並提供已讀 / 未讀切換按鈕。
 *
 * @fires readMessage - 點擊狀態點時切換已讀 / 未讀
 */
import { defineProps, defineEmits } from 'vue';
// eslint-disable-next-line no-unused-vars
import { getDiff } from '../utils/format';
defineEmits(['readMessage']);
const props = defineProps({
  item: {
    type: Object,
    default: () => {},
  },
  readStatus: {
    type: Boolean,
    default: false,
  },
});
</script>
<template>
  <div class="d-flex">
    <div class="d-flex flex-column align-items-center flex-shrink-0">
      <img
        :src="
          props.item.sender_avatar ||
          'https://s.gravatar.com/avatar/4f3bd4380429c206fdd43aec0a6482e0?d=mm'
        "
        alt=""
        width="48"
        height="48"
        class="rounded-circle"
      />
    </div>
    <div class="media-body ms-2 hex-notification-content">
      <p class="mt-0 mb-0" v-html="props.item.content"></p>
      <small
        ><span class="d-inline-block text-muted"
          >${ getDiff(props.item.create_time) }</span
        ></small
      >
    </div>
  </div>
  <div style="width: 10px">
    <span :class="props.readStatus? 'read' : 'un read'" @click="$emit('readMessage', !props.readStatus, props.item.id)"></span>
  </div>
</template>

<style lang="scss" scoped>
.read {
  flex-shrink: 0;
  width: 12px;
  display: block;
  height: 12px;
  border-radius: 50%;
  border: 2px solid var(--bs-hex-primary);
  &.un {
    background: var(--bs-hex-primary);
  }
  &:hover {
    border-color: var(--bs-success);
    background: var(--bs-success);
  }
}
</style>

<spec lang="md">
# 通知項目 (NotificationItem.vue)

## 元件概述
- **用途**：`Notification` 的子元件，顯示單一通知的頭像、內容與相對時間，並提供已讀 / 未讀切換。

## Props
| 名稱 | 類型 | 必填 | 預設值 | 說明 |
|-----|------|-----|--------|------|
| item | `object` | 否 | `() => {}` | 通知資料（含 `sender_avatar`、`content`、`create_time`、`id`） |
| readStatus | `boolean` | 否 | `false` | 是否已讀（決定狀態點樣式） |

## Emits
| 事件名稱 | 參數 | 說明 |
|---------|-----|------|
| readMessage | `(status: boolean, id: number)` | 切換已讀狀態，帶入相反於目前 `readStatus` 的值與訊息 id |

## Slots
無

## 功能行為
- 顯示 `item.sender_avatar`，無值時使用預設 gravatar 頭像
- 以 `v-html` 顯示 `item.content`
- 以 `getDiff(item.create_time)` 顯示相對時間
- 點擊右側狀態點時 emit `readMessage`，帶入 `!readStatus` 與 `item.id`

## 測試行為
- 應顯示通知內容（`item.content`）
- `item.sender_avatar` 有值時，應顯示該頭像；無值時使用預設 gravatar
- 應顯示 `getDiff(item.create_time)` 的相對時間
- 點擊狀態點時，應 emit `readMessage` 並帶 `!readStatus` 與 `item.id`
- `readStatus` 為 true 時狀態點應套用 `read` 樣式，否則為 `un read`

## UI 規格
- 頭像 48×48 圓形（`rounded-circle`）
- 內容區 `.hex-notification-content`，內部連結套用主色
- 右側狀態點為 12×12 圓形，未讀為實心主色、hover 轉為成功色
</spec>
